import React, { useCallback, useState } from "react";
import { useOkapiKy } from "@folio/stripes/core";
import { Button, Col, ConfirmationModal, ErrorModal, Row, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { fetchRegistrationEmailStatus, RegistrationEmailRunningError, startRegistrationEmail, } from "../../api/adminService";
var STATUS_QUERY_KEY = ["admin", "registration-email-status"];
var POLL_INTERVAL_MS = 3000;
var DATE_FORMAT = { dateStyle: "short", timeStyle: "medium" };
/**
 * Admin section that sends the initialization (complete-registration) email to all users: shows how
 * many emails will be sent, asks for confirmation and reports the status of the last run.
 */
export var RegistrationEmail = function () {
    var _a, _b;
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var _c = useIntl(), formatMessage = _c.formatMessage, formatDate = _c.formatDate;
    var _d = useState(false), confirmOpen = _d[0], setConfirmOpen = _d[1];
    var _e = useState(null), errorMessage = _e[0], setErrorMessage = _e[1];
    var showError = useCallback(function (error) {
        console.error("Registration email error:", error);
        setErrorMessage(error instanceof RegistrationEmailRunningError
            ? formatMessage({ id: "ui-br-users.admin.registration.email.error.running" })
            : formatMessage({ id: "ui-br-users.admin.registration.email.error" }, { reason: error.message }));
    }, [formatMessage]);
    var status = useQuery(STATUS_QUERY_KEY, function () { return fetchRegistrationEmailStatus(ky); }, {
        refetchInterval: function (current) { return ((current === null || current === void 0 ? void 0 : current.state) === "RUNNING" ? POLL_INTERVAL_MS : false); },
        onError: showError,
    }).data;
    var _f = useMutation(function () { return startRegistrationEmail(ky); }, {
        onSuccess: function (started) {
            queryClient.setQueryData(STATUS_QUERY_KEY, started);
        },
        onError: function (error) {
            showError(error);
            queryClient.invalidateQueries(STATUS_QUERY_KEY);
        },
    }), start = _f.mutate, isStarting = _f.isLoading;
    var isRunning = (status === null || status === void 0 ? void 0 : status.state) === "RUNNING";
    var count = (_a = status === null || status === void 0 ? void 0 : status.recipientCount) !== null && _a !== void 0 ? _a : 0;
    var openConfirm = useCallback(function () { return setConfirmOpen(true); }, []);
    var closeConfirm = useCallback(function () { return setConfirmOpen(false); }, []);
    var closeError = useCallback(function () { return setErrorMessage(null); }, []);
    var confirmStart = useCallback(function () {
        setConfirmOpen(false);
        start();
    }, [start]);
    var formatTime = function (value) { return (value ? formatDate(value, DATE_FORMAT) : ""); };
    return (React.createElement(React.Fragment, null,
        React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.title", tagName: "h1" }),
        React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.description", tagName: "p" }),
        status && (React.createElement("p", null,
            React.createElement("strong", null,
                React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.recipients", values: { count: count } })))),
        status && status.state !== "IDLE" && (React.createElement("p", null,
            React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.state.".concat(status.state), values: {
                    startedAt: formatTime(status.startedAt),
                    finishedAt: formatTime(status.finishedAt),
                    sent: status.sent,
                    failed: status.failed,
                    error: (_b = status.error) !== null && _b !== void 0 ? _b : "",
                } }))),
        React.createElement(ConfirmationModal, { open: confirmOpen, heading: React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.confirm.heading" }), message: React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.confirm.message", values: { count: count } }), onConfirm: confirmStart, onCancel: closeConfirm, confirmLabel: React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.confirm.confirm" }), cancelLabel: React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.confirm.cancel" }), buttonStyle: "primary" }),
        React.createElement(ErrorModal, { label: React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.title" }), open: errorMessage !== null, content: errorMessage !== null && errorMessage !== void 0 ? errorMessage : "", onClose: closeError }),
        React.createElement(Row, null,
            React.createElement(Col, null,
                React.createElement(Button, { buttonStyle: "primary", onClick: openConfirm, disabled: !status || isStarting || isRunning || count === 0 }, isStarting || isRunning ? (React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.button.loading" })) : (React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.button" })))))));
};
